/**
 * Converts `Location.hoursOfOperation` to and from the `WeekSchedule` the
 * week editor works with. A `path` cannot express this element: one stored
 * entry covers several days, and one day can need several entries (a
 * lunch break).
 */
import type { LocationHoursOfOperation } from "@medplum/fhirtypes";

import type { DayCode } from "@/types/fhir";
import type { TimeRange, WeekSchedule } from "@/types/scheduling";
import { DAY_CODES } from "@/constants/scheduling.constants";
import {
  createEmptyWeekSchedule,
  isDayCode,
  toRangeTime,
  toWireTime,
} from "@/lib/utils/helpers/scheduling";
import { isArray, isObject, isString } from "@/lib/utils/typeof";

/**
 * `allDay` as the widest range the editor can show. No clinic uses it, so a
 * save writing it back as times loses nothing anyone set.
 */
const ALL_DAY_RANGE: TimeRange = { start: "00:00", end: "23:59" };

/**
 * An entry without both times is skipped rather than filled with a default:
 * a half-written range would show hours nobody configured.
 */
const parseEntryRange = (
  entry: LocationHoursOfOperation
): TimeRange | undefined => {
  if (entry.allDay === true) {
    return ALL_DAY_RANGE;
  }

  const { openingTime, closingTime } = entry;
  if (!isString(openingTime) || !isString(closingTime)) {
    return undefined;
  }

  return { start: toRangeTime(openingTime), end: toRangeTime(closingTime) };
};

/**
 * `Location.hoursOfOperation` as a `WeekSchedule`: each entry's range goes
 * on every day it lists. Takes `unknown` because the stored element is read
 * off the resource as is.
 */
export const parseHoursOfOperation = (stored: unknown): WeekSchedule => {
  const schedule = createEmptyWeekSchedule();

  if (!isArray(stored)) {
    return schedule;
  }

  for (const entry of stored) {
    if (!isObject<LocationHoursOfOperation>(entry)) {
      continue;
    }

    const range = parseEntryRange(entry);
    if (!range) {
      continue;
    }

    for (const day of (entry.daysOfWeek ?? []).filter(isDayCode)) {
      schedule[day].enabled = true;
      schedule[day].ranges.push({ ...range });
    }
  }

  return schedule;
};

const byStart = (first: TimeRange, second: TimeRange) =>
  first.start.localeCompare(second.start);

/**
 * The reverse of `parseHoursOfOperation`. Days with the same ranges share
 * one entry per range, so a week of equal weekdays writes one entry, not
 * five. Groups and their days follow `DAY_CODES` order, so the same week
 * always writes the same element.
 *
 * An all-off week returns `undefined`, which removes the element: FHIR has
 * no empty `hoursOfOperation`.
 */
export const serializeHoursOfOperation = (
  schedule: WeekSchedule
): LocationHoursOfOperation[] | undefined => {
  const groups = new Map<string, { days: DayCode[]; ranges: TimeRange[] }>();

  for (const day of DAY_CODES) {
    const { enabled, ranges } = schedule[day];
    if (!enabled || ranges.length === 0) {
      continue;
    }

    const sortedRanges = [...ranges].sort(byStart);
    const key = JSON.stringify(sortedRanges);
    const group = groups.get(key);

    if (group) {
      group.days.push(day);
    } else {
      groups.set(key, { days: [day], ranges: sortedRanges });
    }
  }

  const entries = [...groups.values()].flatMap(({ days, ranges }) =>
    ranges.map(({ start, end }) => ({
      daysOfWeek: [...days],
      openingTime: toWireTime(start),
      closingTime: toWireTime(end),
    }))
  );

  return entries.length > 0 ? entries : undefined;
};
